import { supabase } from '../lib/supabaseClient';

export type TrackingEventType =
  | 'view_vybe'
  | 'vybe_created'
  | 'join_vybe'
  | 'leave_vybe'
  | 'message_sent'
  | 'attendance_confirmed'
  | 'attendance_completed'
  | 'no_show'
  | 'vybe_cancelled'
  | 'profile_completed'
  | 'vybe_shared'
  | 'filter_applied'
  | 'search_performed'
  | 'profile_viewed';

interface TrackEventParams {
  eventType: TrackingEventType;
  userId?: string | null;
  vybeId?: string | null;
  metadata?: Record<string, unknown>;
}

const getSessionId = (): string => {
  let sessionId = sessionStorage.getItem('vybe_session_id');
  if (!sessionId) {
    sessionId = `${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;
    sessionStorage.setItem('vybe_session_id', sessionId);
  }
  return sessionId;
};

export const trackEvent = async ({
  eventType,
  userId,
  vybeId,
  metadata = {}
}: TrackEventParams): Promise<void> => {
  try {
    const sessionId = getSessionId();

    const { error } = await supabase.rpc('track_user_event', {
      p_user_id: userId || null,
      p_vybe_id: vybeId || null,
      p_event_type: eventType,
      p_metadata: metadata,
      p_session_id: sessionId
    });

    if (error) {
      console.warn('Failed to track event:', error.message);
    }
  } catch (err) {
    console.warn('Tracking error:', err);
  }
};

export const trackVybeView = (vybeId: string, userId?: string | null): void => {
  trackEvent({
    eventType: 'view_vybe',
    userId,
    vybeId,
    metadata: { timestamp: new Date().toISOString() }
  });
};

export const trackVybeCreated = (
  vybeId: string,
  userId: string,
  category: string,
  locationBucket?: string
): void => {
  trackEvent({
    eventType: 'vybe_created',
    userId,
    vybeId,
    metadata: { category, location_bucket: locationBucket }
  });
};

export const trackJoinVybe = (
  vybeId: string,
  userId: string,
  applicationId?: string
): void => {
  trackEvent({
    eventType: 'join_vybe',
    userId,
    vybeId,
    metadata: { application_id: applicationId }
  });
};

export const trackLeaveVybe = (
  vybeId: string,
  userId: string,
  reason?: string
): void => {
  trackEvent({
    eventType: 'leave_vybe',
    userId,
    vybeId,
    metadata: { reason }
  });
};

export const trackMessageSent = (
  userId: string,
  conversationId?: string,
  vybeId?: string | null
): void => {
  trackEvent({
    eventType: 'message_sent',
    userId,
    vybeId,
    metadata: { conversation_id: conversationId }
  });
};

export const trackAttendanceConfirmed = (
  vybeId: string,
  userId: string
): void => {
  trackEvent({
    eventType: 'attendance_confirmed',
    userId,
    vybeId
  });
};

export const trackAttendanceCompleted = (
  vybeId: string,
  userId: string
): void => {
  trackEvent({
    eventType: 'attendance_completed',
    userId,
    vybeId
  });
};

export const trackNoShow = (
  vybeId: string,
  userId: string
): void => {
  trackEvent({
    eventType: 'no_show',
    userId,
    vybeId
  });
};

export const trackVybeCancelled = (
  vybeId: string,
  userId: string,
  reason?: string
): void => {
  trackEvent({
    eventType: 'vybe_cancelled',
    userId,
    vybeId,
    metadata: { reason }
  });
};

export const trackProfileCompleted = (
  userId: string,
  fieldsCompleted?: string[]
): void => {
  trackEvent({
    eventType: 'profile_completed',
    userId,
    metadata: { fields_completed: fieldsCompleted }
  });
};

export const trackVybeShared = (
  vybeId: string,
  userId: string | null,
  platform: string
): void => {
  trackEvent({
    eventType: 'vybe_shared',
    userId,
    vybeId,
    metadata: { platform }
  });
};

export const trackFilterApplied = (
  userId: string | null,
  filters: Record<string, unknown>
): void => {
  trackEvent({
    eventType: 'filter_applied',
    userId,
    metadata: { filters }
  });
};

export const trackSearchPerformed = (
  userId: string | null,
  searchTerm: string
): void => {
  trackEvent({
    eventType: 'search_performed',
    userId,
    metadata: { search_term: searchTerm }
  });
};

export const trackProfileViewed = (
  viewerId: string | null,
  viewedUserId: string
): void => {
  trackEvent({
    eventType: 'profile_viewed',
    userId: viewerId,
    metadata: { viewed_user_id: viewedUserId }
  });
};
